
import router from "../../../../src/routes";
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  Ref,
  ref,
  watch
} from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import Card from "../../components/application/shop/Card.vue";
import FilterShop from "../../components/application/shop/Filter.vue";
import PaginationDefault from "../../components/application/shop/PaginationDefault.vue";
import ContentFallback from "../../components/application/moldure/ContentFallback.vue";
import { Prices } from "../../../../domain/entities/shop/filter/prices";
import { Filter } from "../../../../domain/entities/shop/filter/filter";
import { Product } from "../../../../domain/entities/shop/product/product";
import { User } from "../../../../domain/entities/entities";
import { dispatchErrorBox, formatSearchQuery } from "../../components/mixins";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";

export default defineComponent({
  components: {
    Card,
    FilterShop,
    PaginationDefault,
    ContentFallback
  },

  setup() {
    const store = useStore();
    const route = useRoute();

    /** Array used for listing prices with in the filter and to send the entity in the request. */
    const arrayPrices: Prices[] = [
      new Prices(1, "10", 0, 10),
      new Prices(2, "10-100", 10, 100),
      new Prices(3, "100-1000", 100, 1000),
      new Prices(4, "Acima de 1000", 1000, 0)
    ];

    /** Variable that gets the current page of the list of products by route.*/
    const currentPage: Ref<number> = ref(Number(route.params.productsPage));

    /** Variable that gets the current page of the list of products in the favorites by route. */
    const currentPageFavoriteProducts: Ref<number> = ref(
      Number(route.query.page)
    );

    /** Value entered by the user in the search bar. */
    const search: Ref<string> = ref("");

    /** Computed which takes the values of the minPrice and maxPrice inputs set in the store. */
    const inputFilter: ComputedRef<string> = computed(() => {
      return store.getters["shop_module/inputFilter"];
    });

    /** Computed which controls the current state of the filter.
     * In this variable, all filters are grouped.
     * To load the products that are in this variable it is necessary to call the variable through the store. */
    const currentFilter: ComputedRef<Filter> = computed(() => {
      return store.getters["shop_module/currentFilter"];
    });

    /** Computed which returns the number of pages coming from the request.
     * Computed passed as prop to the Pagination component. */
    const pages: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/productsPages"];
    });

    /** List of registered products coming from the request. */
    const products: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/products"];
    });

    /** List of filter selectable entities.
     * Computed passed as a prop to the Filter component. */
    const filter: ComputedRef<Filter> = computed(() => {
      return store.getters["shop_module/filter"];
    });

    /** Computed that returns the products in the bag. */
    const bagProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInBag"];
    });

    /** Variable that receives the lowest value of the filter input. */
    const minPrice: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/minPrice"];
    });

    /** Variable that receives the lowest value of the filter input. */
    const maxPrice: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/maxPrice"];
    });

    /** Getter of the decoins with the subtraction of the values of the products added in the bag. */
    const balance: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/balance"];
    });

    /** Variable to get the user data. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    /** Key that references the timer in progress. Used to cancel the timeout. */
    let timerId;

    onBeforeMount(async () => {
      try {
        await store.dispatch("userProfile_module/loadUser");
        await store.dispatch("shop_module/loadFilters", currentFilter.value);
        await store.dispatch(
          "shop_module/setDecoinsInWallet",
          user.value.dcoins
        );
        await store.dispatch("shop_module/loadFavoriteProducts", {
          page: currentPageFavoriteProducts.value,
          limit: 6
        });

        await store.dispatch("shop_module/loadBagProducts");

        /** Checks if there are shops in the URL. If so, the entity referring to that id is assigned to the shops array.*/
        if (route.query.shop) {
          const queryShop = route.query.shop as Array<string>;
          currentFilter.value.shops = filter.value.shops.filter(e =>
            queryShop.includes(String(e.id))
          );
        }

        /** Checks if there are prices in the URL. If so, the entity referring to that id is assigned to the prices array.*/
        if (route.query.price) {
          const queryPrice = route.query.price as Array<string>;

          currentFilter.value.prices = arrayPrices.filter(e =>
            queryPrice.includes(String(e.id))
          );
        }

        /** Checks if there are categories in the URL. If so, the entity referring to that id is assigned to the shops array.*/
        if (route.query.category) {
          const queryCategory = route.query.category as Array<string>;

          currentFilter.value.categories = filter.value.categories.filter(e =>
            queryCategory.includes(String(e.id))
          );
        }

        if (route.query.min) {
          const minValue = Number(route.query.min);
          await store.dispatch("shop_module/setMinPrice", minValue);
        }

        if (route.query.max) {
          const maxValue = Number(route.query.max);
          await store.dispatch("shop_module/setMaxPrice", maxValue);
        }

        await store.dispatch("shop_module/loadProducts", {
          filter: currentFilter.value,
          page: currentPage.value,
          min: minPrice.value,
          max: maxPrice.value
        });
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    });

    /** Updates the page to the current page. */
    watch(
      () => route.params.productsPage,
      () => {
        if (route.params.productsPage) {
          currentPage.value = Number(route.params.productsPage);
        }
        return;
      }
    );

    /** Updates the current page with the filters in the URL. */
    watch(
      () => currentPage.value,
      async () => {
        await router.push({
          name: "shop",
          params: { productsPage: currentPage.value },
          query: {
            shop: [...currentFilter.value.shops.map(e => e.id)],
            category: [...currentFilter.value.categories.map(e => e.id)],
            price: [...currentFilter.value.prices.map(e => e.id)]
          }
        });
      }
    );

    /** Watch responsible for loading the products according to the filters passed by the URL.*/
    watch(
      () => route.query,
      async () => {
        if (route.query.shop) {
          const queryShop = route.query.shop as Array<string>;

          currentFilter.value.shops = filter.value.shops.filter(e =>
            queryShop.includes(String(e.id))
          );
        }

        if (route.query.category) {
          const queryCategories = route.query.category as Array<string>;

          currentFilter.value.categories = filter.value.categories.filter(e =>
            queryCategories.includes(String(e.id))
          );
        }
        currentFilter.value.searchQuery = search.value;

        try {
          await store.dispatch("shop_module/loadProducts", {
            filter: currentFilter.value,
            page: currentPage.value,
            min: minPrice.value,
            max: maxPrice.value
          });
        } catch (e) {
          if (e instanceof RepositoryException) {
            await dispatchErrorBox(e.message);
          } else {
            console.error(e);
            await dispatchErrorBox("Ocorreu um erro inesperado.");
          }
        }
      }
    );

    /** Function used to search for products by name. */
    async function searchProduct(searchQuery: string): Promise<void> {
      try {
        clearTimeout(timerId);
        timerId = setTimeout(async () => {
          search.value = formatSearchQuery(searchQuery);
          currentFilter.value.searchQuery = search.value;
          await store.dispatch("shop_module/setInputFilter", {
            page: currentPage.value,
            filter: currentFilter.value
          });
          await store.dispatch("shop_module/loadFilters", currentFilter.value);
        }, 500);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    return {
      balance,
      products,
      filter,
      pages,
      inputFilter,
      currentPage,
      bagProducts,
      user,
      search,
      searchProduct
    };
  }
});
